import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { Loader2, AlertCircle, Mail, Lock, ArrowRight, Clock } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading, error: authError, clearError, user, getDefaultRoute } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);

  // Site Sakini için özel yönlendirme
  const getRedirectPath = useCallback(() => {
    if (user?.role === ROLES.SITE_SAKINI || user?.role === ROLES.USER || user?.role === 'site_sakini') {
      return '/resident/dashboard';
    }
    return location.state?.from?.pathname || '/dashboard';
  }, [user, location.state]);

  const from = getRedirectPath();

  useEffect(() => {
    if (isAuthenticated && !authLoading && user) {
      const redirectTo = getRedirectPath();
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, user, getRedirectPath]);

  useEffect(() => {
    clearError();
    setError('');
    setRateLimitInfo(null);
    if (!isAuthenticated) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  }, [clearError, isAuthenticated]);

  useEffect(() => {
    if (rateLimitInfo?.blocked && rateLimitInfo?.remainingMinutes > 0) {
      const timer = setInterval(() => {
        setRateLimitInfo(prev => {
          if (!prev || prev.remainingMinutes <= 1) return null;
          return { ...prev, remainingMinutes: prev.remainingMinutes - 1 };
        });
      }, 60000);
      return () => clearInterval(timer);
    }
  }, [rateLimitInfo?.blocked]);

  const parseRateLimitError = useCallback((errorMessage) => {
    const minuteMatch = errorMessage.match(/(\d+)\s*dakika/i);
    if (minuteMatch) {
      return { blocked: true, remainingMinutes: parseInt(minuteMatch[1], 10) };
    }
    return null;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (rateLimitInfo?.blocked) return;
    
    if (!email || !password) {
      setError('E-posta ve şifre gereklidir');
      return;
    }

    setLoading(true);
    try {
      const loggedInUser = await login(email, password);
      
      // Rol bazlı yönlendirme
      let redirectTo = '/dashboard';
      
      // Site Sakini → Resident Dashboard
      if (loggedInUser?.role === ROLES.SITE_SAKINI || loggedInUser?.role === ROLES.USER || loggedInUser?.role === 'site_sakini') {
        redirectTo = '/resident/dashboard';
      }
      // Çalışan (Usta/Teknisyen) → Usta Portal
      else if (loggedInUser?.role === ROLES.CALISAN || loggedInUser?.role === 'calisan') {
        redirectTo = '/technician-portal';
      }
      
      navigate(redirectTo, { replace: true });
    } catch (err) {
      let errorMsg = err.message || 'Giriş yapılamadı';
      
      // Hata mesajını kullanıcı dostu yap
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('Invalid') || errorMsg.includes('credentials')) {
        errorMsg = 'E-posta veya şifre hatalı. Lütfen kontrol edip tekrar deneyin';
      } else if (errorMsg.includes('404') || errorMsg.includes('not found') || errorMsg.includes('bulunamadı')) {
        errorMsg = 'Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı';
      } else if (errorMsg.includes('Network') || errorMsg.includes('fetch') || errorMsg.includes('Failed')) {
        errorMsg = 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin';
      } else if (errorMsg.includes('500') || errorMsg.includes('Server')) {
        errorMsg = 'Sunucu hatası. Lütfen birkaç dakika sonra tekrar deneyin';
      } else if (errorMsg.includes('disabled') || errorMsg.includes('deaktif')) {
        errorMsg = 'Hesabınız devre dışı bırakılmış. Yöneticinizle iletişime geçin';
      } else if (errorMsg === 'Giriş yapılamadı' || errorMsg.includes('Bilinmeyen') || errorMsg.includes('Unknown')) {
        errorMsg = 'Giriş yapılamadı. E-posta ve şifrenizi kontrol edin';
      }
      
      const rateLimitData = parseRateLimitError(errorMsg);
      if (rateLimitData) {
        setRateLimitInfo(rateLimitData);
        setError('');
      } else {
        setError(errorMsg);
        setRateLimitInfo(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Üst Logo - Orta (sadece desktop) */}
      <div className="hidden lg:flex py-8 justify-center">
        <img 
          src="/hims-logo.png" 
          alt="HIMS" 
          className="h-28 w-auto"
        />
      </div>

      {/* Ana İçerik */}
      <div className="flex-1 flex">
        {/* Sol Görsel - Outdoor */}
        <div className="hidden lg:block lg:w-1/3 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/ev-charger-outdoor.jpg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/80" />
        </div>

        {/* Orta - Giriş Formu */}
        <div className="w-full lg:w-1/3 flex items-center justify-center px-8 bg-slate-900">
          <div className="w-full max-w-sm">
            {/* Mobil - Üstte şarj cihazı görseli ve logo */}
            <div className="lg:hidden text-center mb-6">
              <img 
                src="/ev-charger-outdoor.jpg" 
                alt="HIMS Şarj Cihazı" 
                className="h-28 w-28 mx-auto rounded-2xl object-cover shadow-lg mb-4"
              />
              <img 
                src="/hims-logo.png" 
                alt="HIMS" 
                className="h-12 w-auto mx-auto"
              />
            </div>

            {/* Başlık */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-white">Giriş Yap</h2>
              <p className="text-slate-400 mt-1">Yönetim paneline erişin</p>
            </div>

            {/* Hata Mesajı */}
            {(error || authError) && !rateLimitInfo?.blocked && (
              <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error || authError}</p>
              </div>
            )}

            {/* Rate Limit */}
            {rateLimitInfo?.blocked && (
              <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-amber-400" />
                  <div>
                    <p className="text-amber-300 font-medium">Çok fazla deneme</p>
                    <p className="text-amber-400/80 text-sm">{rateLimitInfo.remainingMinutes} dakika bekleyin</p>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">E-posta</label>
                <div className={`relative ${focusedField === 'email' ? 'ring-1 ring-blue-500' : ''} rounded-lg`}>
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${focusedField === 'email' ? 'text-blue-400' : 'text-slate-500'}`} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    disabled={loading}
                    placeholder="ornek@email.com"
                    className="w-full h-12 pl-11 pr-4 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              {/* Şifre */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Şifre</label>
                <div className={`relative ${focusedField === 'password' ? 'ring-1 ring-blue-500' : ''} rounded-lg`}>
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${focusedField === 'password' ? 'text-blue-400' : 'text-slate-500'}`} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    disabled={loading}
                    placeholder="••••••••"
                    className="w-full h-12 pl-11 pr-4 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>

              {/* Şifremi Unuttum */}
              <div className="text-right">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-slate-400 hover:text-blue-400 transition-colors"
                >
                  Şifremi Unuttum
                </Link>
              </div>

              {/* Giriş Butonu */}
              <button
                type="submit"
                disabled={loading || rateLimitInfo?.blocked}
                data-testid="login-submit-button"
                className="w-full h-12 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : rateLimitInfo?.blocked ? (
                  <span>{rateLimitInfo.remainingMinutes} dk bekleyin</span>
                ) : (
                  <>
                    Giriş Yap
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            {/* Alt yazı */}
            <p className="mt-8 text-center text-sm text-slate-500">
              Akıllı Şarj Yönetim Sistemi
            </p>
            
            {/* Usta Kayıt Linki */}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-center text-sm text-slate-500">
                EV şarj teknisyeni misiniz?{' '}
                <Link to="/register-technician" className="text-primary hover:underline font-medium">
                  Usta olarak kaydolun
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Sağ Görsel - Indoor */}
        <div className="hidden lg:block lg:w-1/3 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/ev-charger-indoor.jpg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-slate-900/60 via-transparent to-slate-900/80" />
        </div>
      </div>

      {/* Alt Copyright */}
      <div className="py-4 text-center">
        <p className="text-xs text-slate-600">© 2026 HIMS</p>
      </div>
    </div>
  );
}
